/*
 * Copyright (c) 2021 Daimler AG. All rights reserved.
 * Implemented 2021 by DCCS GmbH.
 */

import React from 'react';
import { AppBar, Button, Divider, Link, ListItem, Stack, Toolbar, Tooltip } from '@mui/material';
import {
  Link as RouterLink,
  LinkProps as RouterLinkProps,
  useMatch,
  useResolvedPath,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LogoMercedesBenzFinancialServices from '../../themes/img/logo_mbfs.svg';
import { useAuth } from 'react-oidc-context';
import { usePermissions } from '../auth/PermissionsContext';
import { getRelevantNotifcations, useNotificationsBase } from '../global/useNotifications';
import { NotificationLocation, NotificationSeverity } from '../../generated/ApiClient';
import { Error, Info, Warning } from '@mui/icons-material';

/**
 * Component which renders header of the Application.
 */
export function Header() {
  const LinkBehavior = React.forwardRef<
    any,
    Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }
  >((props, ref) => {
    const { href, ...other } = props;
    return <RouterLink ref={ref} to={href} {...other} />;
  });

  function MenuItem(props: { href: string; text: string }) {
    let resolved = useResolvedPath(props.href);
    let match = useMatch({ path: `${resolved.pathname}/*` });

    return (
      <ListItem>
        <Link component={LinkBehavior} href={props.href} className={match ? 'active' : ''}>
          {props.text}
        </Link>
      </ListItem>
    );
  }

  const { t } = useTranslation();
  const auth = useAuth();
  const { hasPermission } = usePermissions();
  const { notifications } = useNotificationsBase();
  const relevantNotifications = getRelevantNotifcations(
    notifications,
    NotificationLocation.Navigation,
    true
  );

  return (
    <>
      <AppBar position="static" className="header">
        <Toolbar variant="regular" className="header-info">
          <div className="header-left">
            <div className="header-logos">
              <img
                src={LogoMercedesBenzFinancialServices}
                alt="Logo Mercedes-Benz Financial Services"
              />
            </div>
            <span className="header-title">
              <Divider orientation="vertical" variant="middle" flexItem />
            </span>
            <div className="header-wrapper">
              <div className="menu-wrapper">
                <div className="menu-desktop">
                  {hasPermission('Dashboard', 'Login') && (
                    <MenuItem href="/dashboard" text={t('header.dashboard')} />
                  )}
                  {hasPermission('Deals', 'View') && (
                    <MenuItem href="/deals" text={t('header.deals')} />
                  )}
                  {hasPermission('Customers', 'View') && (
                    <MenuItem href="/customers" text={t('header.customers')} />
                  )}
                  {hasPermission('Classification.Features', 'View') && (
                    <MenuItem href="/classification" text={t('header.classification')} />
                  )}
                </div>
              </div>
            </div>
          </div>
          <Stack className="header-right" flexDirection="row" gap={1}>
            {relevantNotifications.length > 0 && (
              <Tooltip
                disableFocusListener
                title={
                  <Stack>
                    {relevantNotifications.map((n) => (
                      <span>{n.content}</span>
                    ))}
                  </Stack>
                }
              >
                {relevantNotifications.some((x) => x.severity === NotificationSeverity.Error) ? (
                  <Error color="error" />
                ) : relevantNotifications.some(
                    (x) => x.severity === NotificationSeverity.Warning
                  ) ? (
                  <Warning color="warning" />
                ) : (
                  <Info color="info" />
                )}
              </Tooltip>
            )}
            <span>Version: {process.env.REACT_APP_VERSION ?? 'Development'}</span>
            {auth.isAuthenticated && (
              <>
                <span>|</span>
                {auth.user?.profile &&
                  (auth.user?.profile.given_name || auth.user?.profile.family_name) && (
                    <>
                      <span>{`${auth.user?.profile.given_name ?? ''} ${
                        auth.user?.profile.family_name ?? ''
                      }`}</span>
                      <span>|</span>
                    </>
                  )}
                <span>
                  <Button onClick={() => void auth.signoutRedirect()}>Log out</Button>
                </span>
              </>
            )}
          </Stack>
        </Toolbar>
      </AppBar>
    </>
  );
}
